// client/src/pages/Campaign/campaignLogic.js
// Logique pure de la campagne « Chroniques » R60 — sans JSX, testable partout.
// Consommé par CampaignMap.jsx ; miroir exact des règles serveur (CampaignService).

// ─── Types de nœuds : identifiants d'icônes Icon.jsx (AUCUN emoji) ──────────
export const TYPE_META = {
  PROLOGUE: { icon: 'flag', label: 'Prologue', eyebrow: 'Ouverture' },
  DUELIST: { icon: 'swords', label: 'Duelliste', eyebrow: 'Duel' },
  ELITE: { icon: 'skull', label: 'Élite', eyebrow: 'Élite' },
  EVENT: { icon: 'scroll', label: 'Événement', eyebrow: 'Rencontre' },
  TREASURE: { icon: 'gem', label: 'Trésor', eyebrow: 'Trésor' },
  BOSS: { icon: 'crown', label: 'BOSS', eyebrow: "Boss d'acte" },
};

// ─── Position des nœuds (% sur la carte) : ascension vallée → sommet (boss) ─
export const NODE_POS = [
  { x: 50, y: 93 }, // n0  PROLOGUE — porte de l'acte
  { x: 28, y: 81 }, // n1  DUELIST
  { x: 62, y: 70 }, // n2  EVENT
  { x: 18, y: 57 }, // n3  DUELIST (branche gauche)
  { x: 78, y: 55 }, // n4  DUELIST (branche droite)
  { x: 18, y: 43 }, // n5  TREASURE
  { x: 78, y: 41 }, // n6  ELITE
  { x: 50, y: 33 }, // n7  EVENT (convergence)
  { x: 24, y: 22 }, // n8  DUELIST
  { x: 74, y: 20 }, // n9  TREASURE
  { x: 50, y: 12 }, // n10 ELITE — gardien du sommet
  { x: 50, y: 3 },  // n11 BOSS — sommet
];

// ─── Télégraphe : règles spéciales en FR lisible, jamais de nom technique ───
export const RULE_LABELS = {
  AI_EXTRA_LIFE: (v) => `Adversaire : +${v} PV de départ`,
  AI_EXTRA_CRYSTAL: (v) => `Adversaire : +${v} ${v > 1 ? 'cristaux' : 'cristal'} de départ`,
  PLAYER_START_LIFE: (v) => `Vous commencez à ${v} PV`,
};

export const DIFF_LABELS = { easy: 'Facile', normal: 'Confirmé', hard: 'Difficile', expert: 'Expert' };

// Étoiles accessibles — miroir de resolveDifficulty serveur :
// on ne descend pas sous la base, on monte d'un cran max (boss = fixe).
export const ALLOWED_STARS = { easy: [1, 2], normal: [2, 3], hard: [3], expert: [3] };

export const STAR_LABELS = { 1: 'Découverte', 2: 'Confrontation', 3: 'Exploit' };

// ─── Arts statiques (routes /api/static/images/{nations,banners,duelists}) ──
export const nationArt = (element) => `/api/static/images/nations/nation_${String(element || 'fire').toLowerCase()}.webp`;
export const bannerArt = (actN) => `/api/static/images/banners/banner_act${actN}.webp`;
export const duelistArt = (duelistId) => `/api/static/images/duelists/${duelistId}.webp`;
export const cardBackArt = (element) => `/api/static/images/card-backs/${String(element || 'neutral').toLowerCase()}-back.svg`;

// ─── Helpers purs ────────────────────────────────────────────────────────────

/** Trouve un nœud par id dans toute la campagne. */
export function findNode(campaign, nodeId) {
  if (!campaign || !campaign.acts) return null;
  for (const a of campaign.acts) {
    const n = a.nodes.find((x) => x.id === nodeId);
    if (n) return n;
  }
  return null;
}

/** Nœud courant d'un acte : premier disponible non terminé (l'anneau pulsant). */
export function currentNodeId(nodes) {
  if (!Array.isArray(nodes)) return null;
  const avail = nodes.filter((n) => n.status === 'available');
  return avail.length ? avail[0].id : null;
}

/** Étoiles initiales cohérentes à l'ouverture d'un nœud (déjà gagnées sinon max). */
export function initialStarsFor(node) {
  const allowed = (node.duelist && ALLOWED_STARS[node.duelist.difficulty]) || [2, 3];
  if (node.stars > 0) {
    return Math.min(Math.max(node.stars, allowed[0]), allowed[allowed.length - 1]);
  }
  return allowed[allowed.length - 1];
}

/** Chemin SVG (quadratic bezier doux) entre deux positions de nœuds. */
export function edgePath(p1, p2) {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const d = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const bend = Math.min(6, d * 0.14);
  return `M ${p1.x} ${p1.y} Q ${mx} ${my - bend} ${p2.x} ${p2.y}`;
}

/** Libellé FR d'une règle spéciale (fallback : nom technique masqué en dernier recours). */
export function specialRuleLabel(rule) {
  if (!rule) return '';
  const fn = RULE_LABELS[rule.type];
  return fn ? fn(rule.value) : 'Règle spéciale';
}
